import { Controller } from '@hotwired/stimulus'
import Logger from '../lib/logger'

export default class extends Controller {
  connect () {
    // Use logger instead of console.log
    Logger.debug('Controller connected', { controller: this.constructor.name })
  }

  disconnect () {
    Logger.debug('Controller disconnected', { controller: this.constructor.name })
    this.cleanup()
  }

  focus (element) {
    if (!this.shouldFocus(element)) return
    const value = element.value
    element.focus()
    element.value = ''
    element.value = value
  }
  
  shouldFocus (element) {
    if (document.activeElement === element) return false
    if (element.tagName === 'TEXTAREA') return true
    if (element.tagName !== 'INPUT') return false
    if (element.type === 'hidden') return false
    return true
  }

  // Error handling helper
  handleError(error, context = {}) {
    Logger.error('Controller error', error, { controller: this.constructor.name, ...context })
    
    // Show user-friendly error message if available
    const errorMessage = this.getErrorMessage(error, context)
    if (errorMessage) {
      this.dispatch('error', { detail: { message: errorMessage } })
    }
    
    return false
  }

  // Get user-friendly error message
  getErrorMessage(error, context = {}) {
    if (!error) return null
    
    // Handle different error types
    if (error instanceof TypeError) {
      return 'An unexpected error occurred. Please try again.'
    }
    
    if (error.message) {
      // Check for network errors
      if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        return 'Network error. Please check your connection and try again.'
      }
      
      // Check for timeout errors
      if (error.message.includes('timeout') || error.name === 'AbortError') {
        return 'Request timed out. Please try again.'
      }
      
      // Check for HTTP errors
      if (error.name === 'AbortError') {
        return 'Request was cancelled.'
      }
      
      // Generic error message
      if (error.message.length > 200) {
        return 'An unexpected error occurred. Please try again.'
      }
      
      return error.message
    }
    
    return 'An unexpected error occurred. Please try again.'
  }

  // Safe fetch wrapper with retry logic
  async safeFetch(url, options = {}, maxRetries = 2) {
    const { signal, ...fetchOptions } = options
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        Logger.debug(`Fetch attempt ${attempt + 1}/${maxRetries + 1}`, { url })
        
        const response = await fetch(url, {
          ...fetchOptions,
          signal: signal || this.timeoutSignal(30000) // 30 second timeout
        })
        
        if (!response.ok) {
          Logger.warn('Fetch failed with status', { 
            status: response.status, 
            url,
            controller: this.constructor.name
          })
          
          if (response.status === 429) {
            throw new Error('Too many requests. Please wait and try again.')
          }
          
          if (response.status >= 500) {
            throw new Error(`Server error (${response.status}). Please try again later.`)
          }
        }
        
        return response
      } catch (error) {
        Logger.error('Fetch error', error, { 
          url,
          attempt: attempt + 1,
          controller: this.constructor.name
        })
        
        if (attempt === maxRetries) {
          throw error
        }
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
      }
    }
  }

  // Build an abort signal that fires after `ms` milliseconds. Prefers the
  // native AbortSignal.timeout, but falls back to a manual AbortController
  // since it's absent from some environments (e.g. jsdom in this project's
  // Jest test setup, and older browsers).
  timeoutSignal(ms) {
    if (typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms)
    const controller = new AbortController()
    setTimeout(() => controller.abort(), ms)
    return controller.signal
  }

  // Debounce helper with cleanup
  debounce(func, wait) {
    let timeout
    let lastArgs
    const debounced = (...args) => {
      lastArgs = args
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }

    // Add cancel method
    debounced.cancel = () => {
      clearTimeout(timeout)
      timeout = null
    }

    // Add flush method - immediately invokes with the most recent pending args
    debounced.flush = () => {
      clearTimeout(timeout)
      return lastArgs ? func.apply(this, lastArgs) : undefined
    }

    return debounced
  }

  // Throttle helper with cleanup
  throttle(func, limit) {
    let inThrottle
    const throttled = (...args) => {
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => (inThrottle = false), limit)
      }
    }
    
    throttled.cancel = () => {
      inThrottle = false
    }
    
    return throttled
  }

  // Safe DOM manipulation with error handling
  safeAppend(element, content) {
    try {
      if (element && typeof element.appendChild === 'function') {
        element.appendChild(content)
        return true
      }
      Logger.warn('Cannot append to element', { element })
      return false
    } catch (error) {
      Logger.error('Append failed', error, { element })
      return false
    }
  }

  // Safe DOM query with fallback
  safeQuery(selector, fallback = null) {
    try {
      const element = this.element.querySelector(selector)
      if (!element) {
        Logger.debug('Query returned null', { selector })
      }
      return element || fallback
    } catch (error) {
      Logger.error('Query failed', error, { selector })
      return fallback
    }
  }

  // Safe DOM queryAll with fallback
  safeQueryAll(selector, fallback = []) {
    try {
      const elements = this.element.querySelectorAll(selector)
      return Array.from(elements) || fallback
    } catch (error) {
      Logger.error('QueryAll failed', error, { selector })
      return fallback
    }
  }

  // Safe element removal with error handling
  safeRemove(element) {
    try {
      if (element && element.parentNode) {
        element.remove()
        return true
      }
      Logger.warn('Cannot remove element', { element })
      return false
    } catch (error) {
      Logger.error('Remove failed', error, { element })
      return false
    }
  }

  // Check if element is visible
  isVisible(element) {
    return !!element && element.offsetParent !== null
  }

  // Check if controller is connected
  isConnected() {
    return !!this.element && document.body.contains(this.element)
  }

  // Dispatch event with error handling
  safeDispatch(name, detail = {}) {
    try {
      this.dispatch(name, { detail })
      return true
    } catch (error) {
      Logger.error('Dispatch failed', error, { name })
      return false
    }
  }

  // Cleanup helper for disconnect
  cleanup() {
    Logger.debug('Controller cleanup', { controller: this.constructor.name })
    
    // Cancel any pending debounced functions
    if (this.debounceCleanup) {
      this.debounceCleanup()
      this.debounceCleanup = null
    }
    
    // Remove any event listeners
    if (this.eventListeners) {
      this.eventListeners.forEach(({ element, event, handler, options }) => {
        if (element && element.removeEventListener) {
          element.removeEventListener(event, handler, options)
        }
      })
      this.eventListeners = null
    }
  }

  // Add event listener with cleanup tracking
  addEventListener(element, event, handler, options = {}) {
    if (!this.eventListeners) {
      this.eventListeners = []
    }
    
    const boundHandler = handler.bind(this)
    element.addEventListener(event, boundHandler, options)
    
    this.eventListeners.push({
      element,
      event,
      handler: boundHandler,
      options
    })
  }
}
